/**
 * Catches any render failure of the routed screens, a missing lazy chunk
 * included, so the page is never blank. A missing chunk reloads once first.
 */
import React from 'react';
import { isChunkError, reloadOnce } from './chunkReload';

type State = { error: unknown; reloading: boolean };

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error ?? new Error('unknown') };
  }

  componentDidCatch(error: unknown) {
    if (isChunkError(error) && reloadOnce()) this.setState({ reloading: true });
  }

  render() {
    if (this.state.error == null) return this.props.children;
    if (this.state.reloading) return null;
    const updated = isChunkError(this.state.error);
    return (
      <div role="alert" data-error-boundary style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'var(--surface-sunken)', display: 'grid', placeItems: 'center', padding: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderLeft: '3px solid var(--risk)', borderRadius: 'var(--radius-lg)', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start', maxWidth: 420 }}>
          <b style={{ fontSize: 'var(--text-md)', color: 'var(--ink)' }}>
            {updated ? 'ClimbJobs was updated. Reload to continue.' : 'Something went wrong. Reload to continue.'}
          </b>
          <button type="button" data-error-reload onClick={() => window.location.reload()} style={{ height: 44, padding: '0 20px', border: '1px solid var(--line-strong)', borderRadius: 'var(--radius-pill)', background: 'var(--surface)', fontWeight: 700, fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
